export const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

/** Group key for names that don't start with a letter (digits, symbols). */
export const OTHER_INITIAL = '#';

/**
 * Lowercases and strips accents one character at a time, so the folded
 * string has the same length as the original and a match index found in it
 * maps straight back onto the original name (for highlighting).
 */
export function foldForSearch(text: string): string {
  return Array.from(text, (char) => {
    const folded = char.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
    // Some characters decompose into several (e.g. Hangul); keep those as-is
    // so indexes still line up.
    return folded.length === char.length ? folded : char.toLowerCase();
  }).join('');
}

export interface NameMatch {
  before: string;
  match: string;
  after: string;
}

/** Accent- and case-insensitive substring match; null when it doesn't match. */
export function matchName(name: string, query: string): NameMatch | null {
  const needle = foldForSearch(query.trim());
  if (!needle) return { before: name, match: '', after: '' };
  const at = foldForSearch(name).indexOf(needle);
  if (at < 0) return null;
  return {
    before: name.slice(0, at),
    match: name.slice(at, at + needle.length),
    after: name.slice(at + needle.length),
  };
}

export function initialOf(name: string): string {
  const first = foldForSearch(name.trim().charAt(0)).toUpperCase();
  return ALPHABET.includes(first) ? first : OTHER_INITIAL;
}

export function sortByName<T extends { name: string }>(
  items: readonly T[],
  locale: string
): T[] {
  return [...items].sort((a, b) =>
    a.name.localeCompare(b.name, locale, { sensitivity: 'base' })
  );
}

export interface InitialGroup<T> {
  letter: string;
  items: T[];
}

/** Groups items (already sorted by name) by initial, A–Z, then '#'. */
export function groupByInitial<T extends { name: string }>(
  sorted: readonly T[]
): InitialGroup<T>[] {
  const byLetter = new Map<string, T[]>();
  for (const item of sorted) {
    const letter = initialOf(item.name);
    const group = byLetter.get(letter);
    if (group) group.push(item);
    else byLetter.set(letter, [item]);
  }
  return [...ALPHABET, OTHER_INITIAL]
    .filter((letter) => byLetter.has(letter))
    .map((letter) => ({ letter, items: byLetter.get(letter) ?? [] }));
}
